import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { telemetryProps } from "../../instrumentation/gtm";

const COPIED_STATE_MS = 2000;

type PartField = "mfr" | "shi";

const PART_LABELS: Record<
  PartField,
  { tooltip: string; aria: string; telemetry: string }
> = {
  mfr: {
    tooltip: "Copy mfr part #",
    aria: "Copy manufacturer part number",
    telemetry: "Copy Mfr Part Number",
  },
  shi: {
    tooltip: "Copy SHI part #",
    aria: "Copy SHI part number",
    telemetry: "Copy SHI Part Number",
  },
};

export interface ProductPartNumbersProps {
  mfrPartNumber?: string | null;
  shiPartNumber?: string | null;
  /** Tags the copy actions as `<namePrefix>_copy_mfr_part` / `<namePrefix>_copy_shi_part`; omit to leave them untagged. */
  telemetry?: { namePrefix: string; section: string; id?: string };
}

/**
 * A product's part numbers as one caption line, "mfr · SHI", each copied on click (or Enter/Space).
 * The plain-text pattern from the product detail drawer, which replaces part-number chips.
 */
export function ProductPartNumbers({
  mfrPartNumber,
  shiPartNumber,
  telemetry,
}: ProductPartNumbersProps) {
  const [copied, setCopied] = useState<PartField | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const copy = (field: PartField, value: string) => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(field);
        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(null), COPIED_STATE_MS);
      })
      .catch(() => undefined);
  };

  const part = (field: PartField, value: string) => {
    const labels = PART_LABELS[field];
    return (
      <Tooltip
        title={copied === field ? "Copied!" : labels.tooltip}
        placement="top"
        disableInteractive
      >
        <Typography
          variant="caption"
          color="text.secondary"
          role="button"
          tabIndex={0}
          aria-label={`${labels.aria} ${value}`}
          onClick={() => copy(field, value)}
          onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              copy(field, value);
            }
          }}
          {...(telemetry
            ? telemetryProps({
                name: `${telemetry.namePrefix}_copy_${field}_part`,
                section: telemetry.section,
                label: labels.telemetry,
                id: telemetry.id,
              })
            : {})}
          sx={{
            cursor: "pointer",
            "&:hover, &:focus-visible": { color: "primary.main" },
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    );
  };

  if (!mfrPartNumber && !shiPartNumber) return null;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
      }}
    >
      {mfrPartNumber && part("mfr", mfrPartNumber)}
      {mfrPartNumber && shiPartNumber && (
        <Typography variant="caption" color="text.disabled" aria-hidden="true">
          &middot;
        </Typography>
      )}
      {shiPartNumber && part("shi", shiPartNumber)}
    </Box>
  );
}
